import { useRef, useEffect } from 'react'
import QRCodeStyling from 'qr-code-styling'
import { Download, Share2, Shield } from 'lucide-react'

export default function QRCard({ url, name, plate, type }) {
  const tagline1 = 'Scan to contact'
  const tagline2 = type === 'pet' ? 'the Pet owner' : type === 'child' ? 'the Parent' : 'the vehicle owner'
  const stickerRef = useRef(null)
  const qrRef = useRef(null)
  const qrInstance = useRef(null)

  useEffect(() => {
    const qrUrl = url || 'https://parkping.oneclikai.in'
    qrInstance.current = new QRCodeStyling({
      width: 120,
      height: 120,
      type: 'svg',
      data: qrUrl,
      dotsOptions: { color: '#0a0a0f', type: 'rounded' },
      cornersSquareOptions: { color: '#0a0a0f', type: 'extra-rounded' },
      cornersDotOptions: { color: '#0a0a0f', type: 'dot' },
      backgroundOptions: { color: '#ffffff' },
      imageOptions: { crossOrigin: 'anonymous', margin: 0 },
    })
    if (qrRef.current) {
      qrRef.current.innerHTML = ''
      qrInstance.current.append(qrRef.current)
    }
  }, [url])


  // ── Download: draw sticker manually on canvas (pixel-perfect) ───────────
  const handleSave = async () => {
    const DPR = 3
    const W = 460, H = 190
    const LEFT_W = 268, RIGHT_W = W - LEFT_W
    const BOTTOM_H = 24, MAIN_H = H - BOTTOM_H
    const R = 16 // card corner radius

    const canvas = document.createElement('canvas')
    canvas.width = W * DPR
    canvas.height = H * DPR
    const ctx = canvas.getContext('2d')
    ctx.scale(DPR, DPR)

    const rr = (x, y, w, h, r, fill, stroke, strokeW) => {
      ctx.beginPath()
      ctx.moveTo(x + r, y)
      ctx.lineTo(x + w - r, y)
      ctx.arcTo(x + w, y, x + w, y + r, r)
      ctx.lineTo(x + w, y + h - r)
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
      ctx.lineTo(x + r, y + h)
      ctx.arcTo(x, y + h, x, y + h - r, r)
      ctx.lineTo(x, y + r)
      ctx.arcTo(x, y, x + r, y, r)
      ctx.closePath()
      if (fill) { ctx.fillStyle = fill; ctx.fill() }
      if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = strokeW || 1; ctx.stroke() }
    }

    // Card background (white rounded)
    rr(0, 0, W, H, R, '#ffffff')
    ctx.save(); ctx.clip()

    // Left dark panel
    ctx.fillStyle = '#0a0a0f'
    ctx.fillRect(0, 0, LEFT_W, MAIN_H)

    // P logo box
    rr(16, 14, 28, 28, 7, '#00e5a0')
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 14px system-ui, Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('P', 30, 28)

    // ParkPing wordmark
    ctx.textAlign = 'left'
    ctx.font = 'bold 15px system-ui, Arial'
    ctx.fillStyle = '#ffffff'
    ctx.fillText('Park', 52, 28)
    const parkW = ctx.measureText('Park').width
    ctx.fillStyle = '#00e5a0'
    ctx.fillText('Ping', 52 + parkW, 28)

    // Tagline
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 22px system-ui, Arial'
    ctx.textBaseline = 'top'
    ctx.fillText(tagline1, 16, 56)
    ctx.fillText(tagline2, 16, 84)

    // Plate badge — centered in left panel
    if (plate) {
      ctx.font = '900 13px system-ui, Arial'
      const plateW = ctx.measureText(plate).width + 22
      const plateH = 24
      const plateX = (LEFT_W - plateW) / 2
      const plateY = 122
      rr(plateX, plateY, plateW, plateH, 6, '#fef08a', '#ca8a04', 1.5)
      ctx.fillStyle = '#0a0a0f'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(plate, plateX + plateW / 2, plateY + plateH / 2)
    }

    // Privacy badge
    ctx.fillStyle = '#00e5a0'
    ctx.font = '600 9px system-ui, Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('Privacy protected', LEFT_W / 2, 160)

    // Divider line
    ctx.fillStyle = '#e5e7eb'
    ctx.fillRect(LEFT_W, 0, 2, MAIN_H)

    // Right panel background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(LEFT_W + 2, 0, RIGHT_W, MAIN_H)

    // Draw QR SVG
    const qrSvg = qrRef.current?.querySelector('svg')
    if (qrSvg) {
      const svgData = new XMLSerializer().serializeToString(qrSvg)
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
      const objUrl = URL.createObjectURL(blob)
      await new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const qrSize = 128
          const qrX = LEFT_W + 2 + (RIGHT_W - qrSize) / 2
          const qrY = (MAIN_H - qrSize - 18) / 2
          ctx.drawImage(img, qrX, qrY, qrSize, qrSize)
          URL.revokeObjectURL(objUrl)
          resolve()
        }
        img.onerror = resolve
        img.src = objUrl
      })
    }

    // SCAN ME label
    ctx.fillStyle = '#6b7280'
    ctx.font = 'bold 9px system-ui, Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillText('SCAN ME', LEFT_W + 2 + RIGHT_W / 2, MAIN_H - 8)

    // Bottom green strip
    ctx.fillStyle = '#00e5a0'
    ctx.fillRect(0, MAIN_H, W, BOTTOM_H)
    ctx.fillStyle = '#0a0a0f'
    ctx.font = 'bold 9px system-ui, Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('parkping.in · Privacy-first parking contacts', W / 2, MAIN_H + BOTTOM_H / 2)

    ctx.restore()

    const link = document.createElement('a')
    link.download = `parkping-sticker-${plate?.replace(/\s+/g, '-') || 'tag'}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ParkPing Tag', text: `Scan to contact the owner of ${plate}`, url })
        return
      } catch { /* cancelled */ }
    }
    await navigator.clipboard.writeText(url)
  }

  return (
    <div className="space-y-4">
      {/* ── Sticker card ─────────────────────────────────────────────────── */}
      <div
        ref={stickerRef}
        className="w-full rounded-2xl overflow-hidden"
        style={{
          background: '#ffffff',
          border: '2px solid #e5e7eb',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div className="flex" style={{ minHeight: '160px' }}>

          {/* ── Left panel — branding + message ───────────────────────────── */}
          <div
            style={{
              flex: '1 1 0%',
              background: 'linear-gradient(135deg, #0a0a0f 0%, #13131a 100%)',
              borderRight: '2px solid #e5e7eb',
              padding: '16px',
              overflow: 'hidden',
            }}
          >
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div
                style={{
                  width: '28px', height: '28px', borderRadius: '8px',
                  background: '#00e5a0', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#ffffff', fontWeight: 800,
                  fontSize: '14px', marginRight: '8px', flexShrink: 0,
                }}
              >
                P
              </div>
              <span style={{ color: '#ffffff', fontWeight: 800, fontSize: '15px', letterSpacing: '-0.02em' }}>
                Park<span style={{ color: '#00e5a0' }}>Ping</span>
              </span>
            </div>

            {/* Tagline */}
            <p style={{ color: '#ffffff', fontWeight: 700, fontSize: '21px', lineHeight: '1.3', marginBottom: '10px' }}>
              {tagline1}<br />{tagline2}
            </p>

            {/* Plate — Indian number plate style */}
            {plate && (
              <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                <div
                  style={{
                    background: '#fef08a',
                    borderRadius: '6px',
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    fontWeight: 900,
                    fontSize: '13px',
                    lineHeight: '13px',
                    letterSpacing: '0.14em',
                    color: '#0a0a0f',
                    border: '1.5px solid #ca8a04',
                    display: 'inline-block',
                  }}
                >
                  {plate}
                </div>
              </div>
            )}

            {/* Privacy badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={9} color="#00e5a0" />
              <span style={{ color: '#00e5a0', fontSize: '9px', fontWeight: 600, marginLeft: '4px' }}>
                Privacy protected
              </span>
            </div>
          </div>

          {/* ── Right panel — QR code ──────────────────────────────────────── */}
          <div
            className="flex flex-col items-center justify-center p-4"
            style={{ background: '#ffffff', minWidth: '148px', gap: '6px' }}
          >
            <div ref={qrRef} style={{ lineHeight: 0 }} />
            <p style={{ color: '#6b7280', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' }}>
              Scan me
            </p>
          </div>

        </div>

        {/* ── Bottom strip ─────────────────────────────────────────────────── */}
        <div
          style={{
            background: '#00e5a0',
            padding: '5px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <span style={{ color: '#0a0a0f', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em' }}>
            parkping.in · Privacy-first parking contacts
          </span>
        </div>
      </div>

      {/* ── Action buttons ────────────────────────────────────────────────── */}
      <div className="flex gap-2 w-full">
        <button
          onClick={handleSave}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
          style={{ background: 'rgba(0,229,160,0.15)', color: '#00e5a0', border: '1px solid rgba(0,229,160,0.3)' }}
        >
          <Download size={14} />
          Save Sticker
        </button>
        <button
          onClick={handleShare}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
          style={{ background: 'rgba(0,229,160,0.15)', color: '#00e5a0', border: '1px solid rgba(0,229,160,0.3)' }}
        >
          <Share2 size={14} />
          Share
        </button>
      </div>
    </div>
  )
}
